import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Writing: your one-shots and series. The writing happens on the server, so
// you can close the app while a chapter is being written.

const PenIcon = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);
const PlusIcon = ({ size = 16, color = '#0e0b0f' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <path d="M12 5v14" /><path d="M5 12h14" />
  </svg>
);
const BooksIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 19V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v14" /><path d="M10 19V7a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v12" /><path d="M15.5 7.5l3.4-.9a1 1 0 0 1 1.2.7l2.9 10.7" /><path d="M3 19h18" />
  </svg>
);
const LeafIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 3h9l4 4v14H6z" /><path d="M14 3v5h5" />
  </svg>
);

const num = (n) => Number(n || 0).toLocaleString();
const ago = (iso) => {
  if (!iso) return '';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  if (h < 48) return 'yesterday';
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
};

export default function Writing() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [works, setWorks] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token) { navigate('/'); return undefined; }
    load();
    const id = setInterval(load, 5000);   // chapters being written update themselves
    return () => clearInterval(id);
  }, []);

  const load = () => {
    axios.get(`${API}/writing/works?token=${token}`)
      .then(res => { setWorks(res.data.works || []); setErrorMsg(''); })
      .catch(err => setErrorMsg(err?.response?.data?.detail || "couldn't load your writing"));
  };

  const open = (w) => navigate(w.kind === 'series' ? `/writing/book/${w.id}` : `/writing/p/${w.piece_id}`);
  const busy = (works || []).filter(w => w.busy);

  return (
    <div style={s.page}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap" rel="stylesheet" />
      <style>{`
        .wr-card { transition: border-color .18s ease, transform .12s ease; }
        .wr-card:hover { border-color: rgba(201,147,90,0.32); }
        .wr-card:active { transform: scale(0.99); }
        .wr-card:focus-visible, .wr-btn:focus-visible { outline: 2px solid #c9935a; outline-offset: 2px; }
        .wr-pulse { animation: wrpulse 1.6s ease-in-out infinite; }
        @keyframes wrpulse { 0%,100% { opacity: .45; } 50% { opacity: 1; } }
        @media (prefers-reduced-motion: reduce) { .wr-card, .wr-pulse { transition: none; animation: none; } }
      `}</style>

      <header style={s.header}>
        <button className="wr-btn" style={s.back} onClick={() => navigate('/companions')} aria-label="back">←</button>
        <div style={s.title}>writing</div>
        <button className="wr-btn" style={s.plus} onClick={() => navigate('/writing/new')} aria-label="start something new" title="start something new">
          <PlusIcon />
        </button>
      </header>

      <main style={s.main}>
        <div style={s.hero}>
          <div style={s.heroTitle}>the writing room</div>
          <div style={s.heroSub}>
            give it a premise and what happens. the writer drafts it and the editor cleans it up. then you just talk to it —
            tell it what to change, ask what it thinks — and it keeps working on the story with you.
          </div>
        </div>

        {errorMsg && <div style={s.error}>{errorMsg}</div>}

        {works && works.length === 0 && (
          <button className="wr-card" style={s.empty} onClick={() => navigate('/writing/new')}>
            <PenIcon size={22} color="#c9935a" />
            <span>nothing yet — start your first story</span>
          </button>
        )}

        {busy.length > 0 && <div style={s.sectionLabel}>being written now</div>}
        {busy.map(w => (
          <div key={w.id} className="wr-card" style={s.card} role="button" tabIndex={0}
            onClick={() => navigate(`/writing/p/${w.busy.piece_id}`)} onKeyDown={e => e.key === 'Enter' && navigate(`/writing/p/${w.busy.piece_id}`)}>
            <div style={s.cardTop}>
              <div style={{ minWidth: 0 }}>
                <div style={s.cardTitle}>{w.title}</div>
                <div style={s.cardSub}>{w.kind === 'series' ? `chapter ${w.busy.n}${w.busy.title && !/^Chapter \d+$/.test(w.busy.title) ? ` · ${w.busy.title}` : ''}` : 'one-shot'}</div>
              </div>
              <span className="wr-pulse" style={s.liveDot} aria-hidden="true" />
            </div>
            <div style={s.stageLine}>{w.busy.label}{w.queued ? ` · ${w.queued} more queued` : ''}</div>
            <div style={s.bar}><div style={{ ...s.fill, width: `${Math.round((w.busy.progress || 0) * 100)}%` }} /></div>
          </div>
        ))}

        {works && works.length > 0 && <div style={s.sectionLabel}>everything</div>}
        <div style={s.grid}>
          {(works || []).map(w => (
            <div key={w.id} className="wr-card" style={s.card} role="button" tabIndex={0}
              onClick={() => open(w)} onKeyDown={e => e.key === 'Enter' && open(w)}>
              <div style={s.kind}>
                {w.kind === 'series' ? <BooksIcon /> : <LeafIcon />}
                {w.kind === 'series' ? 'series' : 'one-shot'}
              </div>
              <div style={s.cardTitle}>{w.title}</div>
              {w.world && <div style={s.cardSub}>{w.world}</div>}
              {w.kind === 'series' && (
                <div style={s.dots} aria-label={`${w.written} of ${w.chapters_planned} chapters written`}>
                  {Array.from({ length: Math.min(w.chapters_planned, 24) }, (_, i) => (
                    <span key={i} style={s.dot(i < w.written, w.busy && w.busy.n === i + 1)} />
                  ))}
                </div>
              )}
              <div style={s.cardFoot}>
                <span>{w.kind === 'series' ? `${w.written} of ${w.chapters_planned} chapters · ` : ''}{num(w.words)} words</span>
                <span style={s.when}>{ago(w.updated_at)}</span>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

const s = {
  page: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: '#0e0b0f', color: '#e8ddd0', fontFamily: "'DM Sans', sans-serif", fontWeight: 300 },
  header: {
    position: 'sticky', top: 0, zIndex: 10, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '12px',
    background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)', borderBottom: '1px solid rgba(201,147,90,0.06)',
  },
  back: { width: '36px', height: '36px', borderRadius: '50%', border: '1px solid rgba(201,147,90,0.15)', background: 'transparent', color: '#c9935a', fontSize: '16px', cursor: 'pointer', flexShrink: 0 },
  title: { flex: 1, fontFamily: "'Cormorant Garamond', serif", fontSize: '21px', fontStyle: 'italic' },
  plus: { width: '36px', height: '36px', borderRadius: '50%', border: 'none', background: 'linear-gradient(135deg, #8a5f35, #c9935a)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  main: { maxWidth: '760px', margin: '0 auto', padding: '26px 20px 90px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '12px' },
  hero: { marginBottom: '14px' },
  heroTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: '32px', fontStyle: 'italic', lineHeight: 1.1 },
  heroSub: { fontSize: '13px', color: '#8a7466', lineHeight: 1.6, marginTop: '8px', maxWidth: '540px' },
  sectionLabel: { fontSize: '10px', color: '#6d5d4e', letterSpacing: '0.14em', textTransform: 'uppercase', marginTop: '14px', paddingLeft: '2px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' },
  card: { background: '#1e1825', border: '1px solid rgba(201,147,90,0.12)', borderRadius: '18px', padding: '16px 18px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '8px', textAlign: 'left' },
  cardTop: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' },
  kind: { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '10.5px', color: '#8a7466', letterSpacing: '0.1em', textTransform: 'uppercase' },
  cardTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: '22px', lineHeight: 1.2, color: '#e8ddd0' },
  cardSub: { fontSize: '12px', color: '#8a7466' },
  liveDot: { width: '9px', height: '9px', borderRadius: '50%', background: '#c9935a', marginTop: '8px', flexShrink: 0 },
  stageLine: { fontSize: '12.5px', color: '#c9a57a' },
  bar: { height: '5px', borderRadius: '3px', background: 'rgba(201,147,90,0.12)', overflow: 'hidden' },
  fill: { height: '100%', borderRadius: '3px', background: 'linear-gradient(90deg, #8a5f35, #c9935a)', transition: 'width .6s ease' },
  dots: { display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '2px' },
  dot: (on, live) => ({ width: '16px', height: '4px', borderRadius: '2px', background: on ? '#c9935a' : live ? 'rgba(201,147,90,0.55)' : 'rgba(201,147,90,0.15)' }),
  cardFoot: { display: 'flex', justifyContent: 'space-between', gap: '8px', fontSize: '12px', color: '#9a8676', marginTop: '4px' },
  when: { color: '#6d5d4e', flexShrink: 0 },
  empty: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '40px 20px', borderRadius: '18px', border: '1px dashed rgba(201,147,90,0.25)', background: 'transparent', color: '#8a7466', fontSize: '14px', fontFamily: "'DM Sans', sans-serif", cursor: 'pointer' },
  error: { background: 'rgba(180,60,60,0.12)', border: '1px solid rgba(220,100,100,0.3)', borderRadius: '12px', padding: '10px 14px', fontSize: '12px', color: '#e0a0a0' },
};
